/**
 * 매우 가벼운 마크다운 → HTML 렌더러.
 * 라이브러리 의존성 없이 v1 에서 9 섹션 본문(헤딩/리스트/문단/인용/코드/링크/[n] 인용 마커) 만 처리.
 */
import type { ReactNode } from "react";
import { CitationMarker } from "./CitationMarker";
import type { Source } from "@re/shared";

interface Props {
  markdown: string;
  sources?: Source[];
  onCitationClick?: (n: number) => void;
}

export function Markdown({ markdown, sources = [], onCitationClick }: Props) {
  const lines = markdown.split("\n");
  const out: ReactNode[] = [];
  let listBuffer: string[] = [];
  let inCode = false;
  let codeBuffer: string[] = [];
  let codeLang = "";

  const flushList = (k: string) => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${k}`} className="ml-5 list-disc space-y-1 text-ink/90">
        {listBuffer.map((item, i) => (
          <li key={i}>{renderInline(item, sources, onCitationClick)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  lines.forEach((raw, idx) => {
    if (raw.startsWith("```")) {
      if (inCode) {
        out.push(
          <pre key={`pre-${idx}`} className="overflow-x-auto rounded bg-[color:color-mix(in_oklab,var(--re-text)_8%,transparent)] p-3 text-xs">
            <code data-lang={codeLang}>{codeBuffer.join("\n")}</code>
          </pre>,
        );
        codeBuffer = [];
        codeLang = "";
        inCode = false;
      } else {
        flushList(`${idx}-pre`);
        inCode = true;
        codeLang = raw.slice(3).trim();
      }
      return;
    }
    if (inCode) {
      codeBuffer.push(raw);
      return;
    }

    if (raw.startsWith("### ")) {
      flushList(`${idx}-h3`);
      out.push(<h3 key={idx} className="mt-6 font-heading text-lg">{renderInline(raw.slice(4), sources, onCitationClick)}</h3>);
    } else if (raw.startsWith("## ")) {
      flushList(`${idx}-h2`);
      out.push(<h2 key={idx} className="mt-8 font-heading text-xl">{renderInline(raw.slice(3), sources, onCitationClick)}</h2>);
    } else if (raw.startsWith("# ")) {
      flushList(`${idx}-h1`);
      out.push(<h1 key={idx} className="mt-8 font-heading text-2xl">{renderInline(raw.slice(2), sources, onCitationClick)}</h1>);
    } else if (raw.match(/^\s*[-*]\s+/)) {
      listBuffer.push(raw.replace(/^\s*[-*]\s+/, ""));
    } else if (raw.startsWith("> ")) {
      flushList(`${idx}-q`);
      out.push(
        <blockquote key={idx} className="border-l-4 pl-3 italic text-ink/80" style={{ borderColor: "var(--re-a1)" }}>
          {renderInline(raw.slice(2), sources, onCitationClick)}
        </blockquote>,
      );
    } else if (raw.trim() === "") {
      flushList(`${idx}-blank`);
    } else {
      flushList(`${idx}-p`);
      out.push(<p key={idx} className="leading-relaxed">{renderInline(raw, sources, onCitationClick)}</p>);
    }
  });
  flushList("end");
  return <div className="space-y-3">{out}</div>;
}

function renderInline(
  text: string,
  sources: Source[],
  onCitationClick?: (n: number) => void,
): ReactNode {
  // Tokenize via matchAll: bold **, code ``, links [text](url), citations [n].
  const re = /\*\*[^*]+\*\*|`[^`]+`|\[([^\]]+)\]\(([^)]+)\)|\[(\d+)\]/g;
  const matches = Array.from(text.matchAll(re));
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i]!;
    const start = m.index ?? 0;
    if (start > cursor) parts.push(text.slice(cursor, start));
    const token = m[0];
    const key = `m-${i}-${start}`;
    if (token.startsWith("**")) {
      parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      parts.push(
        <code key={key} className="rounded bg-[color:color-mix(in_oklab,var(--re-text)_8%,transparent)] px-1 font-mono text-xs">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (m[1] && m[2]) {
      parts.push(
        <a key={key} href={m[2]} target="_blank" rel="noreferrer" className="text-a2 underline-offset-2 hover:underline">
          {m[1]}
        </a>,
      );
    } else if (m[3]) {
      const n = parseInt(m[3], 10);
      const src = sources.find((s) => s.n === n);
      parts.push(<CitationMarker key={key} n={n} source={src} onClick={onCitationClick} />);
    }
    cursor = start + token.length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}
